import React, { Component } from 'react';
import { WidgetHelper } from '../WidgetHelper';
import { ERdvService } from '../../Services';
import { FloatLabel } from '../../Components';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { nl } from 'date-fns/locale';
import { ErdvErrors, ErdvErrorsEnum } from '../../Models/Enum/ErdvErrors';
registerLocale('fr', fr);
registerLocale('nl', nl);

// --- Domain types ---

type Site = {
    code: string;
    libelle: string;
};

type Service = {
    key: string;
    libelle: string;
};

type Ressource = {
    id: number;
    key: string;
    type: string;
};

type Horaire = {
    debut: string;
    fin: string;
    ressource: Ressource;
};

type Availability = {
    date: string | Date;
    horaires: Horaire[];
};

type SiteSelection = {
    key: string;
    label: string;
};

type Timing = {
    date: Date;
    hoursStart: string;
    hoursEnd: string;
};

type SelectEvent = {
    target: {
        value: string;
    };
};

// --- Props & State ---

interface IERdvAgencyWidgetProps {
    description: string;
    dictAgencyLabel: string;
    dictDateLabel: string;
    dictHoursLabel: string;
    dictAgencyTitle: string;
    dictAgencyDesc: string;
    dictNoDateLabel: string;
    dictLockTimeError: string;

    error: ErdvErrors | undefined;
    lang: string;
    visitorUID: string;
    site: SiteSelection;
    timing: Timing;
    code: number;
    setSite: (site: SiteSelection) => void;
    setService: (service: SiteSelection) => void;
    setTiming: (timing: Timing, ressource: Ressource) => void;
    showValidateButton: (val: boolean) => void;
}

interface IERdvAgencyWidgetState {
    listSite: Site[] | null;
    listAvailibilities: Availability[] | null;
    listDate: Date[] | null;
    listHours: Horaire[] | null;
    selectedSite: string | null;
    serviceId: string | null;
    date: Date | null;
    time: string | null;
    step: number;
    noAvailabilities: boolean;
}

export class ERdvAgencyWidget extends Component<
    IERdvAgencyWidgetProps,
    IERdvAgencyWidgetState
> {
    constructor(props: IERdvAgencyWidgetProps) {
        super(props);

        this.state = {
            listSite: null,
            listAvailibilities: null,
            listDate: null,
            listHours: null,
            selectedSite: null,
            date: null,
            time: null,
            step: 0,
            serviceId: null,
            noAvailabilities: false,
        };

        this.props.showValidateButton(false);

        void ERdvService.getSites(this.props.code).then((result: Site[]) => {
            result.sort((a, b) => a.libelle.localeCompare(b.libelle));
            this.setState({ listSite: result }, this.setDefaultSite);
        });

        setDefaultLocale('fr');
    }

    floatLabelActive(selectedValue: string | Date | null): boolean {
        return !!selectedValue;
    }

    setDefaultSite = () => {
        if (this.props.site) {
            this.selectSite({
                target: {
                    value: this.props.site.key + ',' + this.props.site.label,
                },
            });
        } else {
            if (this.state.listSite && this.state.listSite.length === 1) {
                this.selectSite({
                    target: {
                        value:
                            this.state.listSite[0].code +
                            ',' +
                            this.state.listSite[0].libelle,
                    },
                });
            }
        }
    };

    selectSite = (event: SelectEvent) => {
        this.setStep(1);
        this.setState({
            time: null,
            date: null,
            listHours: null,
            listDate: null,
            listAvailibilities: null,
        });

        const selectedValue = event.target.value.split(',');
        const siteCode = selectedValue[0];
        this.setState({ selectedSite: selectedValue[0] });
        this.props.setSite({ key: selectedValue[0], label: selectedValue[1] });

        void ERdvService.getServices(
            this.props.code.toString(),
            selectedValue[0]
        ).then((result: Service[]) => {
            if (result.length > 0) {
                const service = result[0];
                const serviceId = service.key;

                this.props.setService({
                    key: service.key,
                    label: service.libelle,
                });
                this.setState({ serviceId: service.key });

                void ERdvService.getAvailabilities(
                    siteCode,
                    this.props.code,
                    serviceId,
                    this.props.visitorUID.toString()
                ).then((result: Availability[]) => {
                    if (result.length > 0) {
                        this.setState(
                            {
                                listAvailibilities: result,
                                listDate: result.map((x) => new Date(x.date)),
                                noAvailabilities: false,
                            },
                            () => {
                                if (this.props.timing) {
                                    this.selectDate(this.props.timing.date);

                                    if (result && result.length > 0) {
                                        const hours = result.find(
                                            (x: Availability) =>
                                                new Date(
                                                    x.date
                                                ).toISOString() ===
                                                this.props.timing.date.toISOString()
                                        );
                                        if (hours && hours.horaires) {
                                            this.setState(
                                                { listHours: hours.horaires },
                                                () => {
                                                    this.selectTime({
                                                        target: {
                                                            value:
                                                                this.props
                                                                    .timing
                                                                    .hoursStart +
                                                                ',' +
                                                                this.props
                                                                    .timing
                                                                    .hoursEnd,
                                                        },
                                                    });
                                                }
                                            );
                                        }
                                    }
                                }
                            }
                        );
                    } else {
                        this.setState({ noAvailabilities: true });
                    }
                });
            }
        });
    };

    selectDate = (date: Date) => {
        this.setStep(1);
        this.props.showValidateButton(false);

        this.setState(
            {
                date,
                time: null,
                listHours: null,
            },
            () => {
                const av = this.state.listAvailibilities?.find((i) =>
                    isSameLocalDay(new Date(i.date), date)
                );

                const horaires = av?.horaires ?? null;

                if (horaires && horaires.length > 0) {
                    this.setState({ listHours: horaires });
                    this.setStep(2);
                }
            }
        );
    };

    selectTime = (event: SelectEvent) => {
        if (!this.state.listHours || this.state.listHours.length === 0) {
            return;
        }
        const selectedValue = event.target.value.split(',');
        const horaire = this.state.listHours.find(
            (i) => i.debut === selectedValue[0]
        );
        if (!horaire) return;

        this.setState({ time: event.target.value });
        this.props.setTiming(
            {
                date: this.state.date!,
                hoursStart: selectedValue[0],
                hoursEnd: selectedValue[1],
            },
            horaire.ressource
        );
        this.props.showValidateButton(true);
    };

    setStep = (step: number) => {
        this.setState({ step });
    };

    render() {
        if (this.state.listSite) {
            return (
                <div className="container">
                    <div className="row">
                        <div className="offset-md-2 col-md-8">
                            {this.props.description === '' && (
                                <>
                                    <h2 className="h2">
                                        {this.props.dictAgencyTitle}
                                    </h2>
                                    <p>{this.props.dictAgencyDesc}</p>
                                </>
                            )}
                            {this.props.description !== '' && (
                                <div
                                    className="mt-5"
                                    dangerouslySetInnerHTML={{
                                        __html: this.props.description,
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="offset-md-2 col-md-8">
                            <FloatLabel
                                active={this.floatLabelActive(
                                    this.state.selectedSite
                                )}
                                label={this.props.dictAgencyLabel}
                                labelId="siteId"
                            >
                                <select onChange={this.selectSite}>
                                    <option disabled selected hidden>
                                        {' '}
                                    </option>
                                    {this.state.listSite.map(
                                        (value: Site, index: number) => (
                                            <option
                                                selected={
                                                    this.state.selectedSite ===
                                                    value.code
                                                }
                                                value={`${value.code},${value.libelle}`}
                                                key={index}
                                            >
                                                {value.libelle}
                                            </option>
                                        )
                                    )}
                                </select>
                            </FloatLabel>
                        </div>
                    </div>

                    {this.state.step > 0 && (
                        <div className="row">
                            <div className="offset-md-2 col-md-8">
                                {!!this.state.listAvailibilities &&
                                    !this.state.noAvailabilities && (
                                        <FloatLabel
                                            active={this.floatLabelActive(
                                                this.state.date
                                            )}
                                            label={this.props.dictDateLabel}
                                            labelId="date"
                                        >
                                            <DatePicker
                                                wrapperClassName="w-100"
                                                locale={this.props.lang}
                                                className="w-100"
                                                dateFormat="dd-MM-yyyy"
                                                minDate={new Date()}
                                                includeDates={
                                                    this.state.listDate ?? []
                                                }
                                                showMonthDropdown
                                                showYearDropdown
                                                selected={this.state.date}
                                                onChange={(date: Date | null) =>
                                                    date &&
                                                    this.selectDate(date)
                                                }
                                                placeholderText={
                                                    this.props.dictDateLabel
                                                }
                                            />
                                        </FloatLabel>
                                    )}
                                {!!this.state.noAvailabilities && (
                                    <FloatLabel
                                        active={true}
                                        label={this.props.dictDateLabel}
                                        labelId="date"
                                    >
                                        <input
                                            disabled
                                            type="text"
                                            className="w-100"
                                            value={this.props.dictNoDateLabel}
                                        />
                                    </FloatLabel>
                                )}
                            </div>
                        </div>
                    )}

                    {this.state.step > 1 && (
                        <div className="row">
                            <div className="offset-md-2 col-md-8">
                                <FloatLabel
                                    active={this.floatLabelActive(
                                        this.state.time
                                    )}
                                    label={this.props.dictHoursLabel}
                                    labelId="hours"
                                >
                                    {!this.state.listHours && (
                                        <input type="text" disabled />
                                    )}
                                    {!!this.state.listHours &&
                                        this.state.listHours.length > 0 && (
                                            <select
                                                onChange={this.selectTime}
                                                value={this.state.time || ''}
                                            >
                                                <option
                                                    disabled
                                                    selected
                                                    hidden
                                                    value=""
                                                >
                                                    {' '}
                                                </option>
                                                {this.state.listHours.map(
                                                    (
                                                        value: Horaire,
                                                        index: number
                                                    ) => (
                                                        <option
                                                            selected={
                                                                this.state
                                                                    .time ===
                                                                `${value.debut},${value.fin}`
                                                            }
                                                            value={`${value.debut},${value.fin}`}
                                                            key={index}
                                                        >
                                                            {value.debut} -{' '}
                                                            {value.fin}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        )}
                                </FloatLabel>
                            </div>
                        </div>
                    )}
                    {this.props.error &&
                        this.props.error.value ===
                            ErdvErrorsEnum.LockTimeError && (
                            <div className="row mt-3">
                                <div className="offset-md-2 col-md-8">
                                    <div
                                        className="alert alert-danger"
                                        role="alert"
                                    >
                                        {this.props.dictLockTimeError}{' '}
                                        {this.props.error.guid}
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            );
        } else {
            return <div>loading</div>;
        }
    }
}

function isSameLocalDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

WidgetHelper.register(ERdvAgencyWidget, 'erdv-agency-widget');
